import { BoxGeometry, Mesh, MeshLambertMaterial, Object3D, Texture } from 'three';
import { CITY } from '../config/settings';
import { pick, randFloat, chance } from '../utils/random';
import { PALETTE } from '../config/settings';
import { TextureLibrary } from '../textures/TextureLibrary';
import { BuildingRegistry } from './BuildingRegistry';

/**
 * Creates a single textured building (and an occasional rooftop cap), adds it
 * to the parent group and registers its collision box. Knows how to build one
 * building well; the city grid decides where buildings go.
 */
export class BuildingFactory {
  constructor(
    private readonly parent: Object3D,
    private readonly textures: TextureLibrary,
    private readonly registry: BuildingRegistry,
  ) {}

  create(x: number, z: number, width: number, depth: number, height: number): void {
    const color = pick(PALETTE.buildings);
    const material = new MeshLambertMaterial({ map: this.facadeFor(color, width, height) });
    const mesh = new Mesh(new BoxGeometry(width, height, depth), material);
    mesh.position.set(x, height / 2 + CITY.CURB_HEIGHT, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.parent.add(mesh);
    this.registry.add(mesh);

    if (chance(0.6)) this.addRooftopCap(x, z, width, depth, height);
  }

  private facadeFor(color: number, width: number, height: number): Texture {
    // Clone so each building can set its own repeat without affecting others;
    // the (cached) source canvas image is shared, so this stays cheap.
    const tex = this.textures.facade(color).clone();
    tex.needsUpdate = true;
    tex.repeat.set(Math.max(1, Math.round(width / 8)), Math.max(1, Math.round(height / 8)));
    return tex;
  }

  private addRooftopCap(x: number, z: number, width: number, depth: number, height: number): void {
    const capHeight = randFloat(3, 10);
    const cap = new Mesh(
      new BoxGeometry(width * randFloat(0.3, 0.6), capHeight, depth * randFloat(0.3, 0.6)),
      new MeshLambertMaterial({ map: this.textures.roof() }),
    );
    cap.position.set(
      x + randFloat(-width * 0.2, width * 0.2),
      height + CITY.CURB_HEIGHT + capHeight / 2,
      z + randFloat(-depth * 0.2, depth * 0.2),
    );
    cap.castShadow = true;
    this.parent.add(cap);
  }
}
